//
// Payment presentation and action-eligibility rules, kept out of the page
// components so they can be reasoned about and tested on their own.
//
// Two rules shape everything here:
//
//   1. The browser never decides what a payment costs. Every amount rendered
//      comes from the payment's `billing_snapshot`, which the backend froze
//      when the link was issued. Nothing in this file multiplies, adds tax, or
//      recomputes a total.
//
//   2. Payment status, payment-link status and email status are three
//      different things and are rendered as three different things. A failed
//      email is not a failed payment, and a customer who paid at the last
//      moment has a captured payment on an expired link.
//
// Disabling an action here is a usability courtesy, not a control. The backend
// re-checks every one of these conditions, which is what actually holds.
//

export const PaymentStatuses = {
  PENDING: "pending",
  CAPTURED: "captured",
  FAILED: "failed",
  REFUNDED: "refunded",
  RECONCILIATION_REQUIRED: "reconciliation_required",
};

export const PaymentLinkStatuses = {
  PENDING: "pending",
  CREATED: "created",
  PARTIALLY_PAID: "partially_paid",
  PAID: "paid",
  EXPIRED: "expired",
  CANCELLED: "cancelled",
  FAILED: "failed",
};

export const EmailStatuses = {
  PENDING: "pending",
  SENT: "sent",
  FAILED: "failed",
  NOT_APPLICABLE: "not_applicable",
};

// The sibling UPI-only collection mechanism offered alongside the Payment
// Link for the same amount -- see constants/payments.js on the backend for
// why it is a genuinely separate Razorpay product with its own lifecycle,
// most notably a 2-hour ceiling instead of the link's 24-hour default, and
// no webhook at all for an unpaid auto-timeout. `expired` is never a value
// the backend stores for this reason: `resolveQrCodeStatus` on the backend,
// mirrored by `isQrCodeExpired` below, derives it from the timestamp on
// every read instead.
export const QrCodeStatuses = {
  NOT_CREATED: "not_created",
  CREATED: "created",
  PAID: "paid",
  CANCELLED: "cancelled",
  EXPIRED: "expired",
  FAILED: "failed",
};

// antd Tag colours, chosen so the three vocabularies cannot be confused at a
// glance: money is green when collected, the link is blue while it is live,
// and anything needing a human is red.
const PAYMENT_STATUS_META = {
  [PaymentStatuses.PENDING]: { label: "Awaiting payment", color: "gold" },
  [PaymentStatuses.CAPTURED]: { label: "Paid", color: "green" },
  [PaymentStatuses.FAILED]: { label: "Failed", color: "red" },
  [PaymentStatuses.REFUNDED]: { label: "Refunded", color: "purple" },
  [PaymentStatuses.RECONCILIATION_REQUIRED]: {
    label: "Needs reconciliation",
    color: "red",
  },
};

const PAYMENT_LINK_STATUS_META = {
  [PaymentLinkStatuses.PENDING]: { label: "Not issued", color: "default" },
  [PaymentLinkStatuses.CREATED]: { label: "Live", color: "blue" },
  [PaymentLinkStatuses.PARTIALLY_PAID]: { label: "Partially paid", color: "orange" },
  [PaymentLinkStatuses.PAID]: { label: "Paid", color: "green" },
  [PaymentLinkStatuses.EXPIRED]: { label: "Expired", color: "default" },
  [PaymentLinkStatuses.CANCELLED]: { label: "Cancelled", color: "default" },
  [PaymentLinkStatuses.FAILED]: { label: "Not created", color: "red" },
};

const EMAIL_STATUS_META = {
  [EmailStatuses.PENDING]: { label: "Sending", color: "gold" },
  // Deliberately "Sent", not "Delivered": the backend knows only that
  // Microsoft Graph accepted the message.
  [EmailStatuses.SENT]: { label: "Sent", color: "green" },
  [EmailStatuses.FAILED]: { label: "Not sent", color: "red" },
  [EmailStatuses.NOT_APPLICABLE]: { label: "—", color: "default" },
};

const QR_CODE_STATUS_META = {
  [QrCodeStatuses.NOT_CREATED]: { label: "Not created", color: "default" },
  [QrCodeStatuses.CREATED]: { label: "Live", color: "blue" },
  [QrCodeStatuses.PAID]: { label: "Paid", color: "green" },
  [QrCodeStatuses.CANCELLED]: { label: "Cancelled", color: "default" },
  [QrCodeStatuses.EXPIRED]: { label: "Expired", color: "default" },
  [QrCodeStatuses.FAILED]: { label: "Not created", color: "red" },
};

/**
 * Look a status up in a display map
 * An unmapped value is shown as itself rather than hidden, so a status the backend adds before the console knows about it is still visible to the reader
 * @param {Object} meta - The display map to read
 * @param {string} status - The stored status
 * @returns {Object} { label, color }
 */
const describe = (meta, status) =>
  meta[status] || {
    label: String(status || "Unknown").replaceAll("_", " "),
    color: "default",
  };

export const describePaymentStatus = (status) => describe(PAYMENT_STATUS_META, status);
export const describePaymentLinkStatus = (status) => describe(PAYMENT_LINK_STATUS_META, status);
export const describeEmailStatus = (status) => describe(EMAIL_STATUS_META, status);
export const describeQrCodeStatus = (status) => describe(QR_CODE_STATUS_META, status || QrCodeStatuses.NOT_CREATED);

/**
 * Whether a payment link has passed its expiry
 * Read from the timestamp rather than from the status, because the status only changes once Razorpay's expiry webhook arrives and the link is unusable before that
 * @param {Object} payment - The payment row
 * @param {Date} now - The instant to judge against
 * @returns {boolean} True when the link can no longer be paid
 */
export const isLinkExpired = (payment, now = new Date()) => {
  if (!payment?.payment_link_expires_at) return false;

  const expiresAt = new Date(payment.payment_link_expires_at);
  if (Number.isNaN(expiresAt.getTime())) return false;

  return expiresAt.getTime() <= now.getTime();
};

/**
 * Whether a customer could still pay this link
 * @param {Object} payment - The payment row
 * @param {Date} now - The instant to judge against
 * @returns {boolean} True when the link is live and unpaid
 */
export const isLinkPayable = (payment, now = new Date()) =>
  payment?.payment_status === PaymentStatuses.PENDING &&
  [
    PaymentLinkStatuses.CREATED,
    PaymentLinkStatuses.PARTIALLY_PAID,
  ].includes(payment?.payment_link_status) &&
  !isLinkExpired(payment, now);

/**
 * Whether a payment's QR code has passed its own expiry
 * Computed from the timestamp rather than trusted from the stored status, mirroring isLinkExpired: Razorpay fires no webhook for an auto-timeout (see QrCodeStatuses above), so a row fetched minutes ago can read "created" when the QR is already dead
 * @param {Object} payment - The payment row
 * @param {Date} now - The instant to judge against
 * @returns {boolean} True when the QR can no longer be scanned and paid
 */
export const isQrCodeExpired = (payment, now = new Date()) => {
  if (!payment?.qr_code_expires_at) return false;

  const expiresAt = new Date(payment.qr_code_expires_at);
  if (Number.isNaN(expiresAt.getTime())) return false;

  return expiresAt.getTime() <= now.getTime();
};

/**
 * The QR status as it should actually read right now
 * Overrides a stale "created" with the derived "expired" state, the same override the backend's serializer applies -- kept here too so a row does not flash "live" for the moment between page load and the next refetch
 * @param {Object} payment - The payment row
 * @param {Date} now - The instant to judge against
 * @returns {string} One of the QrCodeStatuses values
 */
export const resolveQrCodeStatus = (payment, now = new Date()) => {
  const stored = payment?.qr_code_status || QrCodeStatuses.NOT_CREATED;

  if (stored === QrCodeStatuses.CREATED && isQrCodeExpired(payment, now)) {
    return QrCodeStatuses.EXPIRED;
  }

  return stored;
};

/**
 * Whether a customer could still scan and pay this payment's QR code right now
 * @param {Object} payment - The payment row
 * @param {Date} now - The instant to judge against
 * @returns {boolean} True when the QR is live and unpaid
 */
export const isQrCodeUsable = (payment, now = new Date()) =>
  resolveQrCodeStatus(payment, now) === QrCodeStatuses.CREATED;

/**
 * Decide which row actions are available for a payment, and why not when they are not
 *
 * Returns a reason alongside each flag so the UI can explain a disabled action
 * in a tooltip instead of presenting a dead control. Mirrors the guards in
 * VirtueHrm_Admin_BE/services/paymentService.js; the backend is what enforces
 * them.
 *
 * @param {Object} payment - The payment row
 * @param {Object} options - Evaluation options
 * @param {boolean} options.canWrite - Whether the signed-in role may act on payments
 * @param {Date} options.now - The instant to judge against
 * @returns {Object} { resendLink, cancelLink, resendReceipt } each { allowed, reason }
 */
export const resolvePaymentActions = (payment, { canWrite = false, now = new Date() } = {}) => {
  const deny = (reason) => ({ allowed: false, reason });
  const allow = () => ({ allowed: true, reason: null });

  if (!canWrite) {
    const reason = "Only a Master Admin can manage payments";
    return {
      resendLink: deny(reason),
      cancelLink: deny(reason),
      resendReceipt: deny(reason),
      downloadInvoice: deny(reason),
    };
  }

  const captured = payment?.payment_status === PaymentStatuses.CAPTURED;
  const expired = isLinkExpired(payment, now);
  const linkStatus = payment?.payment_link_status;

  const resendLink = (() => {
    if (captured) return deny("This payment has already been paid");
    if (!payment?.payment_link_url) return deny("No payment link was created for this payment");
    if (expired) return deny("The link has expired — generate a new one");
    if (linkStatus === PaymentLinkStatuses.CANCELLED) return deny("The link was cancelled");
    if (linkStatus === PaymentLinkStatuses.EXPIRED) return deny("The link has expired");
    if (linkStatus === PaymentLinkStatuses.FAILED) return deny("The link was never created");
    if (linkStatus === PaymentLinkStatuses.PAID) return deny("The link has already been paid");
    return allow();
  })();

  const cancelLink = (() => {
    if (captured) return deny("A paid payment cannot have its link cancelled");
    if (
      ![PaymentLinkStatuses.CREATED, PaymentLinkStatuses.PARTIALLY_PAID].includes(linkStatus)
    ) {
      return deny(`A ${describePaymentLinkStatus(linkStatus).label.toLowerCase()} link cannot be cancelled`);
    }
    return allow();
  })();

  const resendReceipt = captured
    ? allow()
    : deny("A receipt can only be sent for a verified payment");

  // Always available once a link exists: the invoice is rendered on demand
  // from the payment's own frozen billing_snapshot, so it reads the same
  // whether the link is still live, expired, or already paid.
  const downloadInvoice = payment?.reference_id
    ? allow()
    : deny("No invoice is available for this payment");

  return { resendLink, cancelLink, resendReceipt, downloadInvoice };
};

/**
 * Whether a subscription can have a payment link generated for it
 * A cancelled or deleted subscription is not a renewal candidate; the backend refuses both, and this keeps the console from offering the action
 * @param {Object} subscription - A subscription row from the subscription list
 * @returns {Object} { allowed, reason }
 */
export const canGeneratePaymentLink = (subscription) => {
  if (!subscription) return { allowed: false, reason: "No subscription selected" };
  if (Number(subscription.isDeleted) === 1) {
    return { allowed: false, reason: "This subscription has been deleted" };
  }
  if (String(subscription.status) === "cancelled") {
    return {
      allowed: false,
      reason: "A cancelled subscription cannot be billed. Reactivate it first.",
    };
  }
  // The subscription's own payment status for its current billing period --
  // distinct from any one payment's status. Set to 'paid' by a capture and
  // reset to 'pending' only on expiry, so this blocks re-billing a period
  // that was just settled without blocking an early renewal. Mirrors the
  // backend's own guard in services/paymentBillingService.js; the backend is
  // what actually enforces it.
  if (String(subscription.payment_status) === "paid") {
    return {
      allowed: false,
      reason: "Already paid for its current billing period. A new link can be generated once it is due for renewal.",
    };
  }
  if (!subscription.plan_id || !subscription.customer_id) {
    return { allowed: false, reason: "This subscription has no plan or customer" };
  }
  return { allowed: true, reason: null };
};

/**
 * Resolve the fullest customer record available for a subscription
 *
 * Order matters. The customer embedded on a subscription row is a summary --
 * the list endpoint selects only the columns the table renders -- so
 * preferring it wholesale would mask a field it simply did not ask for. An
 * absent `email` there means "not selected", not "the customer has none", and
 * reading it as the latter is how the payment-link modal used to warn that a
 * customer with a perfectly good address had no email on file. The two
 * sources are merged instead, and an empty value in one can never overwrite
 * a real value in the other.
 *
 * @param {Object} subscription - The subscription row, possibly carrying an embedded customer summary
 * @param {Array<Object>} customers - The separately fetched customer list
 * @returns {Object|null} The merged customer, or null when neither source has one
 */
export const resolveSubscriptionCustomer = (subscription, customers) => {
  const embedded = subscription?.customer || null;
  const full = customers?.find((customer) => customer.id === subscription?.customer_id) || null;

  if (!embedded && !full) return null;

  const present = Object.fromEntries(
    Object.entries(full || {}).filter(
      ([, value]) => value !== null && value !== undefined && value !== ""
    )
  );

  return { ...embedded, ...present };
};

/**
 * Render an amount held in a payment's billing snapshot
 * Prefers the display string the backend already formatted, so the figure in the console is byte-for-byte the figure in the customer's email. Falls back to the minor-unit integer only when a snapshot predates the display fields
 * @param {Object} payment - The payment row
 * @param {string} key - Which amount: base, tax or total
 * @returns {string} The formatted amount, or an em dash
 */
export const formatSnapshotAmount = (payment, key = "amount") => {
  const snapshot = payment?.billing_snapshot || {};
  const displayKey = `${key}_display`;

  if (snapshot[displayKey]) return snapshot[displayKey];

  const minor = snapshot[`${key}_minor`];
  if (!Number.isFinite(Number(minor))) return "—";

  const currency = snapshot.currency || payment?.currency || "";
  const divisor = Number(snapshot.minor_units_per_major) || 100;
  const decimals = String(divisor).length - 1;

  // Only reached by a snapshot written before the display fields existed.
  // toFixed is acceptable here because it is a last-resort read path, not the
  // billing calculation -- that happens on the server, in integer arithmetic.
  return `${currency} ${(Number(minor) / divisor).toFixed(decimals)}`.trim();
};

/**
 * Render the base-and-tax line shown under a payment's total in the history table
 * Returns null when no tax applies, so a tax-free deployment shows the total alone rather than a breakdown implying a rate was charged
 * @param {Object} payment - The payment row
 * @returns {string|null} A line such as "INR 800.00 + 144.00 tax"
 */
export const describeTaxBreakdown = (payment) => {
  const snapshot = payment?.billing_snapshot || {};

  if (!(Number(snapshot.tax_amount_minor) > 0)) return null;

  const currency = snapshot.currency || payment?.currency || "";
  const base = formatSnapshotAmount(payment, "base_amount");
  const tax = formatSnapshotAmount(payment, "tax_amount");

  if (base === "—" || tax === "—") return null;

  // The currency is stated once, on the leading figure. Repeating it on the
  // tax reads as a second, separately denominated amount.
  const prefix = `${currency} `;
  const bareTax = currency && tax.startsWith(prefix) ? tax.slice(prefix.length) : tax;

  return `${base} + ${bareTax} tax`;
};

/**
 * The one date worth showing beside a payment's link status
 *
 * Which date matters depends on what the link did, so the label travels with
 * it. A fixed "Created" column would answer a question nobody asks about a
 * paid payment and would hide the one that actually matters -- when a live
 * link stops working.
 *
 * @param {Object} payment - The payment row
 * @returns {Object|null} { label, value } or null when there is no useful date
 */
export const describeLinkTimestamp = (payment) => {
  if (!payment) return null;

  if (payment.payment_status === PaymentStatuses.CAPTURED && payment.captured_at) {
    return { label: "Paid", value: formatPaymentDate(payment.captured_at) };
  }

  if (isLinkPayable(payment) && payment.payment_link_expires_at) {
    return { label: "Expires", value: formatPaymentDate(payment.payment_link_expires_at) };
  }

  if (payment.payment_link_expires_at && isLinkExpired(payment)) {
    return { label: "Expired", value: formatPaymentDate(payment.payment_link_expires_at) };
  }

  if (payment.createdAt) {
    return { label: "Created", value: formatPaymentDate(payment.createdAt) };
  }

  return null;
};

/**
 * Whether either of a payment's emails failed to send
 * Surfaced in the table as its own marker because the history page no longer carries an email column: "the customer was never told" has to stay visible without opening the row
 * @param {Object} payment - The payment row
 * @returns {Object|null} { label, detail } when an email failed, otherwise null
 */
export const describeEmailTrouble = (payment) => {
  const linkFailed = payment?.payment_link_email_status === EmailStatuses.FAILED;
  const receiptFailed = payment?.receipt_email_status === EmailStatuses.FAILED;

  if (!linkFailed && !receiptFailed) return null;

  const which = linkFailed && receiptFailed
    ? "The payment link and receipt emails"
    : linkFailed
      ? "The payment link email"
      : "The receipt email";

  const error = linkFailed
    ? payment?.payment_link_email_error
    : payment?.receipt_email_error;

  return {
    label: linkFailed && receiptFailed ? "Emails not sent" : linkFailed ? "Link not emailed" : "Receipt not emailed",
    detail: `${which} could not be sent${error ? `: ${error}` : "."} The payment itself is unaffected.`,
  };
};

/**
 * Build the initials shown in a customer's avatar
 * @param {string} name - The customer name
 * @returns {string} One or two uppercase letters, or a dash when there is no name
 */
export const customerInitials = (name) => {
  const words = String(name || "").trim().split(/\s+/).filter(Boolean);

  if (!words.length) return "—";

  return words
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("");
};

/**
 * Summarise the amount breakdown for the confirmation modal and the detail view
 * The tax row is omitted when no tax applies, rather than shown as zero, so a tax-free deployment does not imply a rate was charged
 * @param {Object} snapshotSource - A payment row, or an object carrying a billing_snapshot
 * @returns {Array<Object>} [{ label, value, strong }]
 */
export const buildAmountBreakdown = (snapshotSource) => {
  const snapshot = snapshotSource?.billing_snapshot || {};
  const rows = [
    { label: "Amount before tax", value: formatSnapshotAmount(snapshotSource, "base_amount") },
  ];

  if (Number(snapshot.tax_amount_minor) > 0) {
    rows.push({
      label: `${snapshot.tax_label || "Tax"}${snapshot.tax_rate_display ? ` (${snapshot.tax_rate_display})` : ""}`,
      value: formatSnapshotAmount(snapshotSource, "tax_amount"),
    });
  }

  rows.push({
    label: "Total payable",
    value: formatSnapshotAmount(snapshotSource, "amount"),
    strong: true,
  });

  return rows;
};

/**
 * Format a timestamp for the payment tables
 * @param {string} value - An ISO timestamp
 * @param {Object} options - { withTime } to include the clock time
 * @returns {string} A locale-formatted date, or an em dash
 */
export const formatPaymentDate = (value, { withTime = false } = {}) => {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return withTime ? date.toLocaleString() : date.toLocaleDateString();
};

/**
 * Describe the period a payment covers, from its snapshot or its applied period
 * Prefers the period actually applied at capture over the one the quote predicted: a subscription can lapse between the link being issued and the customer paying, in which case the backend recomputes it
 * @param {Object} payment - The payment row
 * @returns {string} The rendered period, or an em dash
 */
export const describeBillingPeriod = (payment) => {
  const snapshot = payment?.billing_snapshot || {};
  const start = payment?.subscription_period_start || snapshot.period_start;
  const end = payment?.subscription_period_end || snapshot.period_end;

  if (!start || !end) return "—";

  return `${formatPaymentDate(start)} – ${formatPaymentDate(end)}`;
};

// Filter options for the payment history page, spelled exactly as the columns
// store them so a filter can never select nothing.
export const PAYMENT_STATUS_OPTIONS = Object.values(PaymentStatuses).map((value) => ({
  value,
  label: describePaymentStatus(value).label,
}));

export const PAYMENT_LINK_STATUS_OPTIONS = Object.values(PaymentLinkStatuses).map((value) => ({
  value,
  label: describePaymentLinkStatus(value).label,
}));
